/**
 * PromptDescriptionInput
 * Composer component that assembles sub-components for a complete
 * prompt description section: card → preset buttons → textarea → upload → document list.
 *
 * This component maintains backward compatibility while internally
 * delegating to focused sub-components (PromptCard, PresetButtons,
 * PromptTextarea, UploadButton, DocumentList, useDocumentUpload).
 */

import * as React from 'react';
import type { UploadedDocument, PresetButton, PromptFormApiConfig } from '../types';
import { PromptCard } from './PromptCard';
import { PresetButtons } from './PresetButtons';
import { PromptTextarea } from './PromptTextarea';
import { UploadButton } from './UploadButton';
import { DocumentList } from './DocumentList';
import { useDocumentUpload } from './hooks/useDocumentUpload';

const DOCUMENT_ACCEPT = '.pdf,.txt,.doc,.docx,.rtf';

export interface PromptDescriptionInputProps {
    value: string;
    onChange: (value: string) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    activePresetKey: string;
    onActivePresetKeyChange: (key: string) => void;
    presetButtons?: PresetButton[];
    documents: UploadedDocument[];
    onDocumentsChange: (docs: UploadedDocument[]) => void;
    apiConfig?: PromptFormApiConfig;
    disabled?: boolean;
    maxLength?: number;
    placeholder?: string;
    className?: string;
    label?: string;
    labelOptional?: boolean;
}

export const PromptDescriptionInput: React.FC<PromptDescriptionInputProps> = ({
    value,
    onChange,
    onKeyDown,
    activePresetKey,
    onActivePresetKeyChange,
    presetButtons,
    documents,
    onDocumentsChange,
    apiConfig,
    disabled = false,
    maxLength = 500,
    placeholder,
    className,
    label,
    labelOptional = true,
}) => {
    const prevPresetKeyRef = React.useRef(activePresetKey);

    const { uploading, fileInputRef, triggerUpload, handleFileChange, removeDocument } =
        useDocumentUpload({ apiConfig, documents, onDocumentsChange, disabled });

    const activeButton = React.useMemo(
        () => presetButtons?.find((b) => b.key === activePresetKey),
        [presetButtons, activePresetKey]
    );

    const resolvedPlaceholder = React.useMemo(() => {
        if (placeholder) return placeholder;
        return activeButton?.value ?? 'Enter your description...';
    }, [placeholder, activeButton]);

    const isPresetValue = React.useMemo(() => {
        if (!value) return true;
        return presetButtons?.some((b) => b.value === value) ?? true;
    }, [value, presetButtons]);

    const presetsDisabled = disabled || !isPresetValue;

    const handlePresetClick = React.useCallback(
        (btn: PresetButton) => {
            if (btn.key === activePresetKey) return;
            const prevBtn = presetButtons?.find((b) => b.key === prevPresetKeyRef.current);
            const prevPreset = prevBtn?.value ?? '';
            if (!value || value === prevPreset) {
                onChange(btn.value);
            }
            prevPresetKeyRef.current = btn.key;
            onActivePresetKeyChange(btn.key);
        },
        [activePresetKey, value, presetButtons, onChange, onActivePresetKeyChange]
    );

    const handleTextChange = React.useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            if (e.target.value.length <= maxLength) {
                onChange(e.target.value);
            }
        },
        [maxLength, onChange]
    );

    return (
        <PromptCard label={label} labelOptional={labelOptional} className={className}>
            <PresetButtons
                buttons={presetButtons}
                activeKey={activePresetKey}
                onSelect={handlePresetClick}
                disabled={presetsDisabled}
            />
            <PromptTextarea
                value={value}
                onChange={handleTextChange}
                onKeyDown={onKeyDown}
                placeholder={resolvedPlaceholder}
                disabled={disabled}
                maxLength={maxLength}
                uploadSlot={
                    apiConfig ? (
                        <UploadButton
                            onClick={triggerUpload}
                            disabled={disabled}
                            uploading={uploading}
                        />
                    ) : undefined
                }
            />
            <input
                ref={fileInputRef}
                type="file"
                accept={DOCUMENT_ACCEPT}
                multiple
                className="hidden"
                onChange={handleFileChange}
            />
            <DocumentList documents={documents} onRemove={removeDocument} disabled={disabled} />
        </PromptCard>
    );
};

export default PromptDescriptionInput;
